import React from 'react';
import { IconButton, Tooltip, Button } from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Newspaper as NewspaperIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';

/**
 * OpenArticleButton - Componente atômico para abrir o artigo original
 *
 * Este componente exibe um botão que permite abrir o artigo original
 * em uma nova aba do navegador.
 *
 * Para artigos de Jornal/Revista (mídia impressa), redireciona para
 * a página /article/:articleId que mostra o layout especial com zoom.
 */
const OpenArticleButton = ({
  onClick,
  url,
  article, // Novo: objeto artigo completo para verificar mediaType
  disabled = false,
  size = 'small',
  className = '',
  variant = 'icon', // 'icon' | 'button'
  tooltipTitle,
  buttonText,
  sx = {}
}) => {
  // Verificar se é mídia impressa (Jornal/Revista) - case insensitive
  const mediaTypeLower = article?.mediaType?.toLowerCase() || '';
  const isPrintMedia = mediaTypeLower === 'jornal' || mediaTypeLower === 'revista';

  // Definir textos padrão baseado no tipo de mídia
  const defaultTooltip = isPrintMedia ? 'Ver Página Original' : 'Ver Original';
  const defaultButtonText = isPrintMedia ? 'Ver Página Original' : 'Ver Original';

  const finalTooltip = tooltipTitle || defaultTooltip;
  const finalButtonText = buttonText || defaultButtonText;

  const handleClick = (e) => {
    e.stopPropagation();
    if (onClick) {
      onClick();
    } else if (isPrintMedia && article?._id) {
      // Para mídia impressa, abrir a página /article/:articleId com layout especial
      window.open(`/article/${article._id}`, '_blank', 'noopener,noreferrer');
    } else if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Para mídia impressa, o botão está habilitado se tiver article._id
  // Para outros tipos, precisa ter url
  const isDisabled = disabled || (isPrintMedia ? !article?._id : !url);

  // Escolher ícone baseado no tipo de mídia
  const ButtonIcon = isPrintMedia ? NewspaperIcon : OpenInNewIcon;

  if (variant === 'button') {
    return (
      <Button
        startIcon={<ButtonIcon />}
        variant="outlined"
        size={size}
        onClick={handleClick}
        disabled={isDisabled}
        className={className}
      >
        {finalButtonText}
      </Button>
    );
  }

  // Determinar a cor do ícone baseado no sx passado
  const iconColor = sx.color || 'inherit';

  return (
    <Tooltip title={finalTooltip}>
      <span className={`open-article-button ${className}`}>
        <IconButton
          aria-label={finalTooltip}
          onClick={handleClick}
          disabled={isDisabled}
          size={size}
          sx={sx}
        >
          <ButtonIcon sx={{ fontSize: 20, color: iconColor }} />
        </IconButton>
      </span>
    </Tooltip>
  );
};

OpenArticleButton.propTypes = {
  onClick: PropTypes.func,
  url: PropTypes.string,
  article: PropTypes.shape({
    _id: PropTypes.string,
    mediaType: PropTypes.string
  }),
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  className: PropTypes.string,
  variant: PropTypes.oneOf(['icon', 'button']),
  tooltipTitle: PropTypes.string,
  buttonText: PropTypes.string,
  sx: PropTypes.object
};

export default OpenArticleButton;